import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { canSeeAny, homePath, SUGGESTION_TABS } from "./lib/amTabs";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { Skeleton } from "./components/ui/skeleton";
import Layout from "./components/Layout";
import api from "./lib/api";
import Login from "./pages/Login";
import ChangePassword from "./pages/ChangePassword";
import { TwoFactorSetup } from "./components/TwoFactor";

// Pages: Login and ChangePassword load with the app, every other page is
// its own chunk fetched the first time it opens. A deploy deletes the old
// chunks, so a tab opened before it reloads once to get the new ones.
const lazyPage = (load) =>
    lazy(() =>
        load().then((m) => {
            try {
                sessionStorage.removeItem("chunkReload");
            } catch (_) {
                /* ignore */
            }
            return m;
        }, (e) => {
            let reloaded = false;
            try {
                reloaded = sessionStorage.getItem("chunkReload") === "1";
                sessionStorage.setItem("chunkReload", "1");
            } catch (_) {
                /* no storage: reload anyway, once per load */
            }
            if (!reloaded) {
                window.location.reload();
                return new Promise(() => {});
            }
            throw e;
        })
    );
const SRMDashboard = lazyPage(() => import("./pages/SRMDashboard"));
const AMDashboard = lazyPage(() => import("./pages/AMDashboard"));
const Celebrities = lazyPage(() => import("./pages/Celebrities"));
const CelebrityDetail = lazyPage(() => import("./pages/CelebrityDetail"));
const SalesTracking = lazyPage(() => import("./pages/SalesTracking"));
const BoutiqueSort = lazyPage(() => import("./pages/BoutiqueSort"));
const Favorites = lazyPage(() => import("./pages/Favorites"));
const Scrape = lazyPage(() => import("./pages/Scrape"));
const CelebrityCoverage = lazyPage(() => import("./pages/CelebrityCoverage"));
const ReceivedGoods = lazyPage(() => import("./pages/ReceivedGoods"));
const ExclusiveItems = lazyPage(() => import("./pages/ExclusiveItems"));
const ScmDashboard = lazyPage(() => import("./pages/Scm"));
const ScmStock = lazyPage(() => import("./pages/Scm").then((m) => ({ default: m.ScmStock })));
const ScmSlow = lazyPage(() => import("./pages/Scm").then((m) => ({ default: m.ScmSlow })));
const ScmBundles = lazyPage(() => import("./pages/Scm").then((m) => ({ default: m.ScmBundles })));
const ScmExclusives = lazyPage(() => import("./pages/Scm").then((m) => ({ default: m.ScmExclusives })));
const ScmProjects = lazyPage(() => import("./pages/Scm").then((m) => ({ default: m.ScmProjects })));
const ScmPrices = lazyPage(() => import("./pages/Scm").then((m) => ({ default: m.ScmPrices })));
const ExclusivePicks = lazyPage(() => import("./pages/ExclusivePicks"));
const StockAlarm = lazyPage(() => import("./pages/StockAlarm"));
const PhotoSkus = lazyPage(() => import("./pages/PhotoSkus"));
const LookalikeGaps = lazyPage(() => import("./pages/LookalikeGaps"));
const PurchaseBriefs = lazyPage(() => import("./pages/PurchaseBriefs"));
const AmScorecard = lazyPage(() => import("./pages/AmScorecard"));
const SortPerformancePage = lazyPage(() => import("./components/SortPerformance").then((m) => ({ default: m.SortPerformancePage })));
const SrmBrief = lazyPage(() => import("./pages/SrmBrief"));
const Suggestions = lazyPage(() => import("./pages/Suggestions"));
const ComparePrices = lazyPage(() => import("./pages/ComparePrices"));
const SuggestionSettings = lazyPage(() => import("./pages/SuggestionSettings"));
const AdminSettings = lazyPage(() => import("./pages/AdminSettings"));
const SecuritySettings = lazyPage(() => import("./pages/SecuritySettings"));
const WhatsAppSettings = lazyPage(() => import("./pages/WhatsAppSettings"));
const ManageUsers = lazyPage(() => import("./pages/ManageUsers"));
const Compare = lazyPage(() => import("./pages/Compare"));
const Reports = lazyPage(() => import("./pages/Reports"));
const Bundles = lazyPage(() => import("./pages/Bundles"));
const Projects = lazyPage(() => import("./pages/Projects"));
const Mail = lazyPage(() => import("./pages/Mail"));
const Profile = lazyPage(() => import("./pages/Profile"));
const CelebrityDiscovery = lazyPage(() => import("./pages/CelebrityDiscovery"));
const DiscoverySettings = lazyPage(() => import("./pages/DiscoverySettings"));
const SharedDiscovery = lazyPage(() => import("./pages/SharedDiscovery"));

// While a page's code arrives: the page's shape, not a word (py-per 2026-11-02).
const PageLoading = () => (
    <div className="space-y-4 p-1" aria-busy="true" aria-label="Loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20" />
            ))}
        </div>
        <Skeleton className="h-64" />
    </div>
);

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles, tabs }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-background">
                <div className="animate-pulse text-muted-foreground">Loading...</div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/" replace />;
    }

    // A password the SRM set must be replaced before anything opens.
    if (user.must_change_password) {
        return <ChangePassword />;
    }

    // The SRM role must use two-factor sign-in: set it up first.
    if (user.mfa_setup_required) {
        return <TwoFactorSetup />;
    }

    if ((allowedRoles && !allowedRoles.includes(user.role)) || (tabs && !canSeeAny(user, tabs))) {
        return <Navigate to={homePath(user)} replace />;
    }

    return (
        <Layout>
            <Suspense fallback={<PageLoading />}>{children}</Suspense>
        </Layout>
    );
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-background">
                <div className="animate-pulse text-muted-foreground">Loading...</div>
            </div>
        );
    }

    if (user) {
        return <Navigate to={homePath(user)} replace />;
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route
                path="/"
                element={
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                }
            />

            {/* Public share link — no auth, no Layout */}
            <Route path="/share/discovery/:token" element={<SharedDiscovery />} />

            {/* SRM Dashboard */}
            <Route
                path="/dashboard/srm"
                element={
                    <ProtectedRoute allowedRoles={["SRM"]}>
                        <SRMDashboard />
                    </ProtectedRoute>
                }
            />

            {/* AM Dashboard */}
            <Route
                path="/dashboard/am"
                element={
                    <ProtectedRoute allowedRoles={["SRM"]}>
                        <AMDashboard />
                    </ProtectedRoute>
                }
            />

            {/* Celebrities */}
            <Route
                path="/celebrities"
                element={
                    <ProtectedRoute allowedRoles={["SRM", "AM"]} tabs={["celebrities"]}>
                        <Celebrities />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/celebrities/:id"
                element={
                    <ProtectedRoute allowedRoles={["SRM"]}>
                        <CelebrityDetail />
                    </ProtectedRoute>
                }
            />

            {/* Sales */}
            <Route
                path="/coverage"
                element={
                    <ProtectedRoute allowedRoles={["SRM", "AM"]} tabs={["coverage"]}>
                        <CelebrityCoverage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/scrape"
                element={
                    <ProtectedRoute allowedRoles={["SRM", "AM"]} tabs={["scrape"]}>
                        <Scrape />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/boutique-sort"
                element={
                    <ProtectedRoute allowedRoles={["SRM", "AM"]} tabs={["sort"]}>
                        <BoutiqueSort />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/favorites"
                element={
                    <ProtectedRoute allowedRoles={["SRM", "AM"]} tabs={["favorites"]}>
                        <Favorites />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/sales/tracking"
                element={
                    <ProtectedRoute allowedRoles={["SRM"]}>
                        <SalesTracking />
                    </ProtectedRoute>
                }
            />

            {/* Senior category manager: his departments (the SRM opens any department) */}
            <Route
                path="/scm"
                element={
                    <ProtectedRoute allowedRoles={["SCM", "SRM"]}>
                        <ScmDashboard />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/scm/stock"
                element={
                    <ProtectedRoute allowedRoles={["SCM", "SRM"]}>
                        <ScmStock />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/scm/slow"
                element={
                    <ProtectedRoute allowedRoles={["SCM", "SRM"]}>
                        <ScmSlow />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/scm/bundles"
                element={
                    <ProtectedRoute allowedRoles={["SCM", "SRM"]}>
                        <ScmBundles />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/scm/exclusives"
                element={
                    <ProtectedRoute allowedRoles={["SCM", "SRM"]}>
                        <ScmExclusives />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/scm/projects"
                element={
                    <ProtectedRoute allowedRoles={["SCM", "SRM"]}>
                        <ScmProjects />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/scm/prices"
                element={
                    <ProtectedRoute allowedRoles={["SCM", "SRM"]}>
                        <ScmPrices />
                    </ProtectedRoute>
                }
            />

            {/* Bundles */}
            <Route
                path="/bundles"
                element={
                    <ProtectedRoute allowedRoles={["SRM", "AM"]} tabs={["bundles"]}>
                        <Bundles />
                    </ProtectedRoute>
                }
            />

            {/* Projects (from the project app): each AM their own, SRM all */}
            <Route
                path="/projects"
                element={
                    <ProtectedRoute allowedRoles={["SRM", "AM"]} tabs={["projects"]}>
                        <Projects />
                    </ProtectedRoute>
                }
            />

            {/* Mail - SRM Only */}
            <Route
                path="/mail"
                element={
                    <ProtectedRoute allowedRoles={["SRM"]}>
                        <Mail />
                    </ProtectedRoute>
                }
            />

            {/* Inventory - SRM Only */}
            <Route
                path="/inventory/received"
                element={
                    <ProtectedRoute allowedRoles={["SRM"]}>
                        <ReceivedGoods />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/photo-skus"
                element={
                    <ProtectedRoute allowedRoles={["SRM", "AM"]} tabs={["photos"]}>
                        <PhotoSkus />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/inventory/stock-alarm"
                element={
                    <ProtectedRoute allowedRoles={["SRM", "AM"]} tabs={["stock"]}>
                        <StockAlarm />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/srm-brief"
                element={
                    <ProtectedRoute allowedRoles={["SRM"]}>
                        <SrmBrief />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/am-scorecard"
                element={
                    <ProtectedRoute allowedRoles={["SRM", "AM"]} tabs={["scorecard"]}>
                        <AmScorecard />
                    </ProtectedRoute>
                }
            />
            {/* AMs: their own boutiques' sort moves and next moves (the SRM has it as a tab of Boutique Sort). */}
            <Route
                path="/sort-performance"
                element={
                    <ProtectedRoute allowedRoles={["SRM", "AM"]} tabs={["sortperf"]}>
                        <SortPerformancePage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/purchase-briefs"
                element={
                    <ProtectedRoute allowedRoles={["SRM"]}>
                        <PurchaseBriefs />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/lookalike-gaps"
                element={
                    <ProtectedRoute allowedRoles={["SRM", "AM"]} tabs={["gaps"]}>
                        <LookalikeGaps />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/inventory/exclusive-picks"
                element={
                    <ProtectedRoute allowedRoles={["SRM", "AM"]} tabs={["exclusives"]}>
                        <ExclusivePicks />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/inventory/exclusive"
                element={
                    <ProtectedRoute allowedRoles={["SRM", "AM"]} tabs={["exclusives"]}>
                        <ExclusiveItems />
                    </ProtectedRoute>
                }
            />

            {/* Suggestions */}
            <Route
                path="/suggestions"
                element={
                    <ProtectedRoute allowedRoles={["SRM", "AM"]} tabs={SUGGESTION_TABS}>
                        <Suggestions />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/compare-prices"
                element={
                    <ProtectedRoute allowedRoles={["SRM"]}>
                        <ComparePrices />
                    </ProtectedRoute>
                }
            />

            {/* Celebrity Discovery - SRM Only */}
            <Route
                path="/discovery"
                element={
                    <ProtectedRoute allowedRoles={["SRM"]}>
                        <CelebrityDiscovery />
                    </ProtectedRoute>
                }
            />

            {/* Settings - SRM Only */}
            <Route
                path="/settings/discovery"
                element={
                    <ProtectedRoute allowedRoles={["SRM"]}>
                        <DiscoverySettings />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings/suggestions"
                element={
                    <ProtectedRoute allowedRoles={["SRM"]}>
                        <SuggestionSettings />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings/admin"
                element={
                    <ProtectedRoute allowedRoles={["SRM"]}>
                        <AdminSettings />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings/security"
                element={
                    <ProtectedRoute allowedRoles={["SRM"]}>
                        <SecuritySettings />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings/whatsapp"
                element={
                    <ProtectedRoute allowedRoles={["SRM"]}>
                        <WhatsAppSettings />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings/users"
                element={
                    <ProtectedRoute allowedRoles={["SRM"]}>
                        <ManageUsers />
                    </ProtectedRoute>
                }
            />

            {/* Compare */}
            <Route
                path="/compare"
                element={
                    <ProtectedRoute allowedRoles={["SRM"]}>
                        <Compare />
                    </ProtectedRoute>
                }
            />

            {/* Reports */}
            <Route
                path="/reports"
                element={
                    <ProtectedRoute allowedRoles={["SRM"]}>
                        <Reports />
                    </ProtectedRoute>
                }
            />

            {/* Profile - All Users */}
            <Route
                path="/profile"
                element={
                    <ProtectedRoute>
                        <Profile />
                    </ProtectedRoute>
                }
            />

            {/* Catch all - redirect to login */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

// A page that throws while drawing used to blank the whole app (black screen, nothing
// reported). Show the error with a reload button, and send it to the backend log.
class CrashGuard extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        try {
            api.post("/client-error", {
                message: String(error?.message || error),
                stack: `${error?.stack || ""}\n${info?.componentStack || ""}`.slice(0, 4000),
                path: window.location.pathname + window.location.search,
                agent: navigator.userAgent,
            }).catch(() => null);
        } catch (_) {
            /* ignore */
        }
    }

    render() {
        if (!this.state.error) return this.props.children;
        return (
            <div style={{ padding: 32, color: "#eee", fontFamily: "system-ui", maxWidth: 720 }}>
                <h2 style={{ fontSize: 20, marginBottom: 8 }}>This page hit an error</h2>
                <pre style={{ whiteSpace: "pre-wrap", fontSize: 13, opacity: 0.8 }}>{String(this.state.error?.message || this.state.error)}</pre>
                <button
                    type="button"
                    onClick={() => window.location.reload()}
                    style={{ marginTop: 16, padding: "8px 16px", borderRadius: 6, background: "#fff", color: "#000" }}
                >
                    Reload
                </button>
            </div>
        );
    }
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                {/* Public pages (shared discovery) load outside Layout. */}
                <CrashGuard>
                    <Suspense fallback={<PageLoading />}>
                        <AppRoutes />
                    </Suspense>
                </CrashGuard>
                <Toaster position="top-right" richColors />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
